import { useState } from 'react';
import { api, type CronJob } from '../lib/api';
import { cronText } from '../lib/format';

/** Fertige Zeitpläne statt eines Cron-Eingabefelds. Wer wirklich etwas
 *  Exotisches braucht, tippt es unter "Eigener Ausdruck" — aber niemand
 *  muss Cron können, um alle sechs Stunden suchen zu lassen. */
const PLAENE = [
  { wert: '0 * * * *', text: 'stündlich' },
  { wert: '0 */3 * * *', text: 'alle 3 Stunden' },
  { wert: '0 */6 * * *', text: 'alle 6 Stunden' },
  { wert: '0 */12 * * *', text: 'alle 12 Stunden' },
  { wert: '0 4 * * *', text: 'täglich um 04:00' },
  { wert: '0 4 * * 1', text: 'montags um 04:00' },
];

const AUFLOESUNGEN = ['', 'SD', '720p', '1080p', '2160p'];

export function JobDialog({
  job, aufSchliessen, aufFertig, neuTrotzVorlage = false,
}: {
  job: CronJob | null;
  aufSchliessen: () => void;
  aufFertig: () => void | Promise<void>;
  /** Vorbefuellt, aber noch nicht gespeichert — kommt aus einem Suchtreffer.
   *  Ohne das wuerde der Dialog PATCH auf die Schein-Id 0 schicken. */
  neuTrotzVorlage?: boolean;
}) {
  const neu = job === null || neuTrotzVorlage;
  const [f, setF] = useState({
    name: job?.name ?? '',
    search_query: job?.search_query ?? '',
    search_type: job?.search_type ?? 'tv',
    cron_expression: job?.cron_expression ?? '0 */6 * * *',
    auto_download: job?.auto_download ?? true,
    preferred_resolution: job?.preferred_resolution ?? '',
    quality_cutoff: job?.quality_cutoff ?? '',
    upgrade_propers: job?.upgrade_propers ?? true,
    target_season: job?.target_season != null ? String(job.target_season) : '',
    min_size_mb: job?.min_size_mb != null ? String(job.min_size_mb) : '',
    max_size_mb: job?.max_size_mb != null ? String(job.max_size_mb) : '',
  });
  const [eigenerPlan, setEigenerPlan] = useState(
    !PLAENE.some((p) => p.wert === (job?.cron_expression ?? '0 */6 * * *')),
  );
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setzen = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((alt) => ({ ...alt, [k]: v }));

  async function speichern() {
    if (!f.name.trim() || !f.search_query.trim()) {
      setFehler('Name und Suchbegriff sind Pflicht.');
      return;
    }
    setSpeichert(true);
    setFehler(null);
    const zahl = (s: string) => (s.trim() === '' ? null : Number(s));
    const daten = {
      name: f.name.trim(),
      search_query: f.search_query.trim(),
      search_type: f.search_type,
      cron_expression: f.cron_expression.trim(),
      auto_download: f.auto_download,
      preferred_resolution: f.preferred_resolution || null,
      quality_cutoff: f.quality_cutoff || null,
      upgrade_propers: f.upgrade_propers,
      target_season: zahl(f.target_season),
      min_size_mb: zahl(f.min_size_mb),
      max_size_mb: zahl(f.max_size_mb),
    };
    try {
      if (neu) await api('/cron-jobs', { method: 'POST', body: JSON.stringify(daten) });
      // PUT, nicht PATCH: Der Router kennt nur PUT (`@router.put("/{job_id}")`)
      // und antwortet auf PATCH mit 405. Inhaltlich ist es trotzdem eine
      // Teilaktualisierung — das Backend nimmt `exclude_unset=True` und
      // ueberschreibt nur die mitgeschickten Felder. Alle anderen
      // Speicher-Aufrufe der App (Benutzer, Einstellungen) nutzen ebenfalls PUT.
      else await api(`/cron-jobs/${job.id}`, { method: 'PUT', body: JSON.stringify(daten) });
      await aufFertig();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <div className="overlay" onClick={aufSchliessen}>
      <div className="karte dialog" onClick={(e) => e.stopPropagation()}>
        <div className="karte__kopf">
          <span className="karte__titel">{neu ? 'Neuer Auftrag' : f.name || 'Auftrag bearbeiten'}</span>
        </div>

        <div className="dialog__leib stapel">
          {fehler && <div className="hinweis hinweis--schlecht">{fehler}</div>}

          <div className="gitter-2">
            <div>
              <label className="label" htmlFor="j-name">Name</label>
              <input id="j-name" className="feld" value={f.name}
                onChange={(e) => setzen('name', e.target.value)}
                placeholder="z. B. The Boys Staffel 5" />
            </div>
            <div>
              <label className="label" htmlFor="j-query">Suchbegriff beim Indexer</label>
              <input id="j-query" className="feld mono" value={f.search_query}
                onChange={(e) => setzen('search_query', e.target.value)}
                placeholder="The Boys 2019" />
            </div>
          </div>

          <div className="gitter-2">
            <div>
              <label className="label" htmlFor="j-typ">Art</label>
              <select id="j-typ" className="feld" value={f.search_type}
                onChange={(e) => setzen('search_type', e.target.value)}>
                <option value="tv">Serie — folgt Folgen fortlaufend</option>
                <option value="movie">Film — beste Fassung einmalig</option>
                <option value="general">Allgemein</option>
              </select>
            </div>
            {f.search_type === 'tv' && (
              <div>
                <label className="label" htmlFor="j-staffel">Staffel (leer = alle)</label>
                <input id="j-staffel" className="feld" inputMode="numeric" value={f.target_season}
                  onChange={(e) => setzen('target_season', e.target.value)} placeholder="5" />
              </div>
            )}
          </div>

          <div>
            <label className="label" htmlFor="j-plan">Wie oft suchen?</label>
            {!eigenerPlan ? (
              <select id="j-plan" className="feld" value={f.cron_expression}
                onChange={(e) => setzen('cron_expression', e.target.value)}>
                {PLAENE.map((p) => <option key={p.wert} value={p.wert}>{p.text}</option>)}
              </select>
            ) : (
              <input id="j-plan" className="feld mono" value={f.cron_expression}
                onChange={(e) => setzen('cron_expression', e.target.value)} placeholder="0 */6 * * *" />
            )}
            <div className="reihe reihe--verteilt" style={{ marginTop: 6 }}>
              <span className="klein schwach">Ergibt: {cronText(f.cron_expression)}</span>
              <button className="knopf knopf--klein" type="button"
                onClick={() => setEigenerPlan(!eigenerPlan)}>
                {eigenerPlan ? 'Vorlagen zeigen' : 'Eigener Ausdruck'}
              </button>
            </div>
          </div>

          <div className="karte" style={{ padding: 14 }}>
            <div className="reihe reihe--verteilt" style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 650 }}>Automatisch laden</span>
              <button className="schalter" type="button" data-an={f.auto_download}
                onClick={() => setzen('auto_download', !f.auto_download)}
                aria-label="Automatisch laden"><i /></button>
            </div>
            <div className="klein schwach">
              Aus = der Auftrag sucht nur und meldet Treffer, lädt aber nichts.
            </div>
          </div>

          {/* Qualitaetsregeln. In der alten Oberflaeche gab es sie ueberhaupt
              nicht — dabei entscheiden genau sie, ob eine Folge ein zweites
              Mal geladen wird. Deshalb stehen sie hier mit Klartext dabei. */}
          <div className="karte" style={{ padding: 14 }}>
            <div style={{ fontWeight: 650, marginBottom: 10 }}>Qualität</div>
            <div className="gitter-2">
              <div>
                <label className="label" htmlFor="j-res">Mindestauflösung</label>
                <select id="j-res" className="feld" value={f.preferred_resolution}
                  onChange={(e) => setzen('preferred_resolution', e.target.value)}>
                  {AUFLOESUNGEN.map((r) => (
                    <option key={r} value={r}>{r || 'egal'}</option>
                  ))}
                </select>
                <div className="klein schwach" style={{ marginTop: 5 }}>
                  Untergrenze, keine feste Vorgabe: Gibt es nichts darüber, wird
                  trotzdem geladen, statt die Folge zu verpassen.
                </div>
              </div>
              <div>
                <label className="label" htmlFor="j-cut">Aufwerten bis</label>
                <select id="j-cut" className="feld" value={f.quality_cutoff}
                  onChange={(e) => setzen('quality_cutoff', e.target.value)}>
                  {AUFLOESUNGEN.map((r) => (
                    <option key={r} value={r}>{r || 'nicht aufwerten'}</option>
                  ))}
                </select>
                <div className="klein schwach" style={{ marginTop: 5 }}>
                  Ohne Grenze bleibt die zuerst geladene Fassung für immer stehen.
                  Mit Grenze wird einmalig auf diese Stufe nachgebessert — und dann Schluss.
                </div>
              </div>
            </div>

            <div className="reihe reihe--verteilt" style={{ marginTop: 14 }}>
              <div>
                <div style={{ fontWeight: 600 }}>Korrigierte Fassungen holen</div>
                <div className="klein schwach">
                  Ein PROPER oder REPACK ersetzt eine fehlerhafte Aufnahme.
                </div>
              </div>
              <button className="schalter" type="button" data-an={f.upgrade_propers}
                onClick={() => setzen('upgrade_propers', !f.upgrade_propers)}
                aria-label="PROPER und REPACK holen"><i /></button>
            </div>

            <div className="gitter-2" style={{ marginTop: 14 }}>
              <div>
                <label className="label" htmlFor="j-min">Mindestgröße (MB)</label>
                <input id="j-min" className="feld" inputMode="numeric" value={f.min_size_mb}
                  onChange={(e) => setzen('min_size_mb', e.target.value)} placeholder="leer = egal" />
              </div>
              <div>
                <label className="label" htmlFor="j-max">Höchstgröße (MB)</label>
                <input id="j-max" className="feld" inputMode="numeric" value={f.max_size_mb}
                  onChange={(e) => setzen('max_size_mb', e.target.value)} placeholder="leer = egal" />
              </div>
            </div>
          </div>
        </div>

        <div className="dialog__fuss">
          <button className="knopf" onClick={aufSchliessen} disabled={speichert}>Abbrechen</button>
          <button className="knopf knopf--haupt" onClick={speichern} disabled={speichert}>
            {speichert ? 'Speichert …' : neu ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
